import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Heading, Text } from "@nbs/ui";
import { RoleContextBanner } from "@/features/rbac/RoleContextBanner";

/** Public URL of the committed source PDF (copied into web dist at build time). */
export const SOURCE_PDF_HREF = "/docs/ECOSYSTEMIQUE_NBS.pdf";
export const SOURCE_PDF_FILENAME = "ECOSYSTEMIQUE_NBS.pdf";

/**
 * Admin-only hub: locate the original UICN/ELA PDF and jump into its
 * digitized platform surfaces (N1, catalogs, dashboard).
 */
export function SourceReportPage() {
  const { t } = useTranslation();

  return (
    <article className="nbs-source-report" data-testid="source-report-page">
      <header className="nbs-source-report__header">
        <Heading level={1}>{t("sourceReport.title")}</Heading>
        <Text tone="muted">{t("sourceReport.description")}</Text>
      </header>

      <RoleContextBanner />

      <section className="nbs-settings-card" aria-labelledby="source-file-heading">
        <Heading level={2} id="source-file-heading">
          {t("sourceReport.file.title")}
        </Heading>
        <dl className="nbs-settings-card__meta">
          <div>
            <dt>{t("sourceReport.file.name")}</dt>
            <dd>
              <code>{SOURCE_PDF_FILENAME}</code>
            </dd>
          </div>
          <div>
            <dt>{t("sourceReport.file.path")}</dt>
            <dd>
              <code>apps/nbs-platform/{SOURCE_PDF_FILENAME}</code>
            </dd>
          </div>
          <div>
            <dt>{t("sourceReport.file.pages")}</dt>
            <dd>160</dd>
          </div>
        </dl>
        <Text tone="muted" size="sm">
          {t("sourceReport.file.lead")}
        </Text>
        <div className="nbs-source-report__actions">
          <a href={SOURCE_PDF_HREF} target="_blank" rel="noopener noreferrer">
            <Button type="button" variant="primary">
              {t("sourceReport.file.openPdf")}
            </Button>
          </a>
          <a href={SOURCE_PDF_HREF} download={SOURCE_PDF_FILENAME}>
            <Button type="button" variant="secondary">
              {t("sourceReport.file.downloadPdf")}
            </Button>
          </a>
        </div>
      </section>

      <section className="nbs-settings-card" aria-labelledby="source-digital-heading">
        <Heading level={2} id="source-digital-heading">
          {t("sourceReport.digital.title")}
        </Heading>
        <Text tone="muted" size="sm">
          {t("sourceReport.digital.lead")}
        </Text>
        <ul className="nbs-source-report__links">
          <li>
            <Link className="nbs-text-link" to="/n1">
              {t("sourceReport.digital.n1")}
            </Link>
            <Text tone="muted" size="sm" as="span">
              {" — "}
              {t("sourceReport.digital.n1Hint")}
            </Text>
          </li>
          <li>
            <Link className="nbs-text-link" to="/indicators">
              {t("sourceReport.digital.indicators")}
            </Link>
            <Text tone="muted" size="sm" as="span">
              {" — "}
              {t("sourceReport.digital.indicatorsHint")}
            </Text>
          </li>
          <li>
            <Link className="nbs-text-link" to="/measures">
              {t("sourceReport.digital.measures")}
            </Link>
            <Text tone="muted" size="sm" as="span">
              {" — "}
              {t("sourceReport.digital.measuresHint")}
            </Text>
          </li>
          <li>
            <Link className="nbs-text-link" to="/dashboard">
              {t("sourceReport.digital.dashboard")}
            </Link>
            <Text tone="muted" size="sm" as="span">
              {" — "}
              {t("sourceReport.digital.dashboardHint")}
            </Text>
          </li>
          <li>
            <Link className="nbs-text-link" to="/review">
              {t("sourceReport.digital.review")}
            </Link>
            <Text tone="muted" size="sm" as="span">
              {" — "}
              {t("sourceReport.digital.reviewHint")}
            </Text>
          </li>
        </ul>
      </section>
    </article>
  );
}
